import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Alert, Spinner, Badge, Table } from 'react-bootstrap';
import { FaVial, FaPlay, FaCogs, FaBroom, FaHistory, FaTrash, FaPlus, FaLayerGroup } from 'react-icons/fa';
import aiModelApi from '../../services/aiModelApi';
import * as api from '../../services/api';

const ModelTesting = ({ setError, setSuccess }) => {
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [availableModels, setAvailableModels] = useState([]);
  const [levelOptions, setLevelOptions] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  
  // Test State
  const [prompt, setPrompt] = useState('');
  const [messages, setMessages] = useState([
    { role: 'system', content: 'You are a helpful assistant.' },
    { role: 'user', content: '' }
  ]);
  const [mode, setMode] = useState('simple'); // 'simple' or 'conversation'
  const [testConfig, setTestConfig] = useState({
    llm_level: 1,
    llm_key: '',
    temperature: 0.7,
    max_tokens: 1000,
    use_level: true // defaulting to true for level-based routing
  });
  
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchAgents();
  }, []);

  // Fetch models when agent is selected
  useEffect(() => {
    if (selectedAgentId) {
      fetchAvailableModels(selectedAgentId);
    } else {
      setAvailableModels([]);
      setLevelOptions([]);
    }
  }, [selectedAgentId]);

  const fetchAgents = async () => {
    try {
      const response = await api.getAgents();
      let agentsList = [];
      if (response && response.agents) {
        agentsList = response.agents;
      } else if (response && response.body && response.body.agents) {
        agentsList = response.body.agents;
      }
      setAgents(agentsList);
      
      if (agentsList.length > 0 && !selectedAgentId) {
        setSelectedAgentId(agentsList[0].uniqueIdentifier);
      }
    } catch (e) {
      console.error('Error loading agents', e);
      setError('Failed to load agents: ' + e.message);
    }
  };

  const fetchAvailableModels = async (agentId) => {
    if (!agentId) return;

    setLoadingModels(true);
    try {
      console.log('Fetching available models for agent:', agentId);
      
      // FIX: Pass just the agent ID, not an object. The service handles payload construction.
      const response = await aiModelApi.modelTesting.getAvailableModels(parseInt(agentId));

      let models = [];
      
      // Robust response handling for various formats including stringified body
      // 1. Try direct data access (if api.js already parsed it)
      if (response && response.success && response.data && response.data.models) {
        models = response.data.models;
      }
      // 2. Try parsing body if it's a string (raw response handling)
      else if (response && response.body) {
        try {
          const body = typeof response.body === 'string' ? JSON.parse(response.body) : response.body;
          if (body.success && body.data?.models) {
            models = body.data.models;
          } else if (body.models) {
            models = body.models;
          }
        } catch (parseError) {
          console.error('Error parsing response body:', parseError);
        }
      } 
      // 3. Fallbacks for other structures
      else if (response && response.data?.models) {
        models = response.data.models;
      } else if (response && response.models) {
        models = response.models;
      }
      
      console.log(`Loaded ${models.length} models for testing`);
      setAvailableModels(models);
      
      if (models.length > 0) {
        // Extract unique levels with their associated models
        const levelsMap = new Map();
        
        models.forEach(model => {
          // Handle all valid levels including 0
          if (model.llm_level !== undefined && model.llm_level !== null) {
            // If multiple models share a level, we keep the one we found first or just map them
            // Since we need one dropdown item per level, we map by level
            if (!levelsMap.has(model.llm_level)) {
              levelsMap.set(model.llm_level, {
                level: model.llm_level,
                category: model.category,
                sub_category: model.sub_category,
                agent_utilization: model.agent_utilization,
                model_name: model.model_name,
                provider: model.provider,
                llm_key: model.llm_key
              });
            }
          }
        });
        
        // Convert to array and sort by level (ascending)
        const levels = Array.from(levelsMap.values()).sort((a, b) => a.level - b.level);
        console.log('Sorted levels for dropdown:', levels);
        setLevelOptions(levels);
        
        // Set default level if current selection is invalid or not set
        const currentLevelExists = levels.some(l => l.level === parseInt(testConfig.llm_level));
        
        if (levels.length > 0 && (!currentLevelExists || testConfig.llm_level === undefined)) {
          setTestConfig(prev => ({ 
            ...prev, 
            llm_level: levels[0].level,
            use_level: true
          }));
        }
      } else {
        setLevelOptions([]);
      }
    } catch (e) {
      console.error('Error loading models', e);
      setError('Failed to load available models: ' + e.message);
      setLevelOptions([]);
    } finally {
      setLoadingModels(false);
    }
  };

  const handleConfigChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTestConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleMessageChange = (index, value) => {
    const newMessages = [...messages];
    newMessages[index].content = value;
    setMessages(newMessages);
  };

  const addMessage = () => {
    setMessages([...messages, { role: 'user', content: '' }]);
  };

  const removeMessage = (index) => {
    if (messages.length > 1) {
      setMessages(messages.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAgentId) {
      setError("Please select an agent");
      return;
    }

    setTesting(true);
    setResult(null);

    const clientJobId = `test-${Date.now()}`;
    
    // Construct LLM Request with strict level-based routing
    const llmRequest = {
      temperature: parseFloat(testConfig.temperature),
      max_tokens: parseInt(testConfig.max_tokens),
      // Always use level-based routing as per requirements
      llm_level: parseInt(testConfig.llm_level),
      agent_id: parseInt(selectedAgentId)
    };

    if (mode === 'simple') {
      if (!prompt.trim()) {
        setError("Prompt is required");
        setTesting(false);
        return;
      }
      llmRequest.prompt = prompt;
    } else {
      const validMessages = messages.filter(m => m.content.trim() !== '');
      if (validMessages.length === 0) {
        setError("At least one message is required");
        setTesting(false);
        return;
      }
      llmRequest.messages = validMessages;
    }

    try {
      const response = await aiModelApi.modelTesting.submitJob({
        client_job_id: clientJobId,
        llm_request: llmRequest
      });

      if (response && response.success) {
        setResult(response.data);
        // Add to local history
        setHistory(prev => [response.data, ...prev].slice(0, 10));
        setSuccess("Test completed successfully");
      } else {
        setError(response?.error?.message || response?.error || "Test failed");
        setResult({ error: response?.error || "Unknown error" });
      }
    } catch (err) {
      console.error("Test execution error:", err);
      setError("Test execution failed: " + err.message);
      setResult({ error: { message: err.message } });
    } finally {
      setTesting(false);
    }
  };

  const clearResult = () => {
    setResult(null);
  };

  const getSelectedAgentName = () => {
    const agent = agents.find(a => a.uniqueIdentifier === selectedAgentId);
    return agent ? (agent.agentName || agent.repoName) : 'Unknown Agent';
  };

  const getLevelLabel = (levelInfo) => {
    // Combine level with model name as requested: "Level X - Model Name"
    const modelStr = levelInfo.model_name ? ` - ${levelInfo.model_name}` : '';
    // Add truncated category if available to help context
    const categoryStr = levelInfo.category ? ` (${levelInfo.category.split(' ')[0]})` : '';
    
    return `Level ${levelInfo.level}${modelStr}${categoryStr}`;
  };

  return (
    <div className="model-testing">
      {/* Enhanced Horizontal Test Configuration */}
      <div className="dashboard-filters-container mb-4">
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', 
          gap: 'var(--spacing-md)', 
          alignItems: 'end' 
        }}>
          <div className="filter-group">
            <label className="filter-label">Target Agent</label>
            <Form.Select 
              value={selectedAgentId} 
              onChange={(e) => setSelectedAgentId(e.target.value)}
              required
              className="filter-control"
            >
              <option value="">Select Agent...</option>
              {agents.map(a => (
                <option key={a.uniqueIdentifier} value={a.uniqueIdentifier}>
                  {a.agentName || a.repoName}
                </option>
              ))}
            </Form.Select>
          </div>
          
          <div className="filter-group">
            <label className="filter-label">Mode</label>
            <Form.Select 
              value={mode} 
              onChange={(e) => setMode(e.target.value)}
              className="filter-control"
            >
              <option value="simple">Simple Prompt</option>
              <option value="conversation">Conversation</option>
            </Form.Select>
          </div>
          
          <div className="filter-group">
            <label className="filter-label">Temperature</label>
            <Form.Control 
              type="number" 
              step="0.1" 
              min="0" 
              max="1"
              name="temperature" 
              value={testConfig.temperature} 
              onChange={handleConfigChange}
              className="filter-control"
            />
          </div>
          
          <div className="filter-group">
            <label className="filter-label">Max Tokens</label>
            <Form.Control 
              type="number" 
              step="100"
              name="max_tokens" 
              value={testConfig.max_tokens} 
              onChange={handleConfigChange} 
              min="1"
              className="filter-control"
            />
          </div>
          
          <div className="filter-group">
            <Button 
              variant="primary" 
              onClick={handleSubmit} 
              disabled={testing || !selectedAgentId || levelOptions.length === 0}
              className="w-100"
            >
              {testing ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Testing...
                </>
              ) : (
                <>
                  <FaPlay className="me-2" />
                  Run Test
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Model/Level Selection Row - Simplified to just Level Dropdown */}
      <div className="dashboard-filters-container mb-4">
        <div className="filter-group w-100">
          <label className="filter-label d-flex align-items-center">
            <FaLayerGroup className="me-2 text-primary" />
            LLM Level Routing (Select Level & Model)
          </label>
          <div className="d-flex align-items-center">
            <Form.Select 
              name="llm_level" 
              value={testConfig.llm_level} 
              onChange={handleConfigChange}
              disabled={loadingModels || levelOptions.length === 0}
              className="filter-control custom-level-select"
              style={{ maxWidth: '100%', fontSize: '1rem', padding: '0.75rem' }}
            >
              {loadingModels ? (
                <option value="">Loading routing levels...</option>
              ) : levelOptions.length === 0 ? (
                <option value="">No routing levels configured for this agent</option>
              ) : (
                levelOptions.map(levelInfo => (
                  <option key={levelInfo.level} value={levelInfo.level}>
                    {getLevelLabel(levelInfo)}
                  </option>
                ))
              )}
            </Form.Select>
            {loadingModels && <Spinner size="sm" className="ms-3 text-info" />}
          </div>
          <div className="text-muted small mt-2">
            Select the routing level. The system will use the specific model assigned to this level for processing.
          </div>
        </div>
      </div>

      <Row>
        <Col lg={6}>
          <Card className="ai-card h-100">
            <div className="ai-card-header">
              <h4 className="ai-card-title">
                <FaCogs />
                Test Input
              </h4>
              {selectedAgentId && (
                <Badge bg="info" className="ai-badge">
                  Context: {getSelectedAgentName()}
                </Badge>
              )}
            </div>
            <div className="ai-card-body">
              {mode === 'simple' ? (
                <Form.Group>
                  <Form.Label>Test Prompt</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={12} 
                    value={prompt} 
                    onChange={(e) => setPrompt(e.target.value)} 
                    placeholder="Enter your test prompt here..."
                    required
                    style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                  />
                </Form.Group>
              ) : (
                <div className="conversation-editor">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <Form.Label>Conversation Messages</Form.Label>
                    <Button variant="outline-secondary" size="sm" onClick={addMessage}>
                      <FaPlus className="me-1" />
                      Add Message
                    </Button>
                  </div>
                  {messages.map((msg, idx) => (
                    <div key={idx} className="mb-3 p-3 border rounded bg-dark d-flex gap-2 align-items-start">
                      <div style={{ minWidth: '100px' }}>
                        <Form.Select 
                          size="sm" 
                          value={msg.role} 
                          onChange={(e) => {
                            const newMsgs = [...messages];
                            newMsgs[idx].role = e.target.value;
                            setMessages(newMsgs);
                          }}
                        >
                          <option value="system">System</option>
                          <option value="user">User</option>
                          <option value="assistant">Assistant</option>
                        </Form.Select>
                      </div>
                      <Form.Control 
                        as="textarea" 
                        rows={3} 
                        value={msg.content} 
                        onChange={(e) => handleMessageChange(idx, e.target.value)}
                        className="flex-grow-1"
                        placeholder={`Enter ${msg.role} message...`}
                        style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                      />
                      {messages.length > 1 && (
                        <Button 
                          variant="outline-danger" 
                          size="sm" 
                          onClick={() => removeMessage(idx)}
                          title="Remove message"
                        >
                          <FaTrash />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="ai-card h-100">
            <div className="ai-card-header">
              <h4 className="ai-card-title">
                <FaVial />
                Test Results
              </h4>
              {result && (
                <Button variant="outline-secondary" size="sm" onClick={clearResult}>
                  <FaBroom className="me-2" />
                  Clear
                </Button>
              )}
            </div>
            <div className="ai-card-body d-flex flex-column h-100">
              {testing ? (
                <div className="text-center p-5 text-muted">
                  <Spinner animation="grow" variant="primary" />
                  <p className="mt-3">Waiting for LLM response...</p>
                  <small>This may take a few moments depending on the model and complexity</small>
                </div>
              ) : result ? (
                result.error ? (
                  <Alert variant="danger">
                    <h6>Test Failed</h6>
                    <pre className="mt-2 p-2 bg-dark rounded text-white" style={{ whiteSpace: 'pre-wrap' }}>
                      {typeof result.error === 'string' ? result.error : JSON.stringify(result.error, null, 2)}
                    </pre>
                  </Alert>
                ) : (
                  <div className="result-display">
                    <Alert variant="success" className="mb-3">
                      <Row>
                        <Col md={4}>
                          <strong>Status:</strong> {result.status || 'completed'}
                        </Col>
                        <Col md={4}>
                          <strong>Duration:</strong> {parseFloat(result.duration_seconds || 0).toFixed(2)}s
                        </Col>
                        <Col md={4}>
                          <strong>Model:</strong> {result.model_used || 'Unknown'}
                        </Col>
                      </Row>
                      <Row className="mt-2">
                        <Col>
                          <strong>Tokens:</strong> {parseFloat(result.input_tokens || 0).toLocaleString()} In / {parseFloat(result.output_tokens || 0).toLocaleString()} Out
                        </Col>
                        <Col>
                          <strong>Job ID:</strong> <small>{result.job_id || 'N/A'}</small>
                        </Col>
                      </Row>
                    </Alert>
                    
                    <h6 className="text-white mb-2">Response Content</h6>
                    <div className="test-result-pre p-3 border rounded bg-dark" style={{ minHeight: '150px', maxHeight: '400px', overflowY: 'auto' }}>
                      <pre className="mb-0 text-white" style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                        {result.result?.content || result.result?.response || result.response || result.content || (typeof result.result === 'string' ? result.result : 'No response content available')}
                      </pre>
                    </div>
                    
                    {result.metadata && (
                      <div className="mt-3">
                        <h6 className="text-white mb-2">Metadata</h6>
                        <div className="test-result-pre p-2 bg-dark rounded border border-secondary" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                          <pre className="mb-0 small text-muted">
                            {JSON.stringify(result.metadata, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                )
              ) : (
                <div className="text-center p-5 text-muted">
                  <FaVial size={48} className="mb-3 opacity-50" />
                  <p>Configure your test inputs and select a level to run a test.</p>
                  <small>Results will include response content and performance metrics.</small>
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Recent Test History */}
      {history.length > 0 && (
        <Row className="mt-4">
          <Col>
            <Card className="ai-card">
              <div className="ai-card-header">
                <h5 className="ai-card-title">
                  <FaHistory />
                  Recent Test History
                </h5>
                <Badge bg="info" className="ai-badge">
                  {history.length} test{history.length !== 1 ? 's' : ''}
                </Badge>
              </div>
              <div className="ai-card-body">
                <div className="table-responsive">
                  <Table className="ai-table" size="sm" hover>
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Job ID</th>
                        <th>Model</th>
                        <th>Duration</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((job, i) => (
                        <tr key={i}>
                          <td className="small text-muted">
                            {new Date().toLocaleTimeString()}
                          </td>
                          <td className="small">
                            {job.job_id?.substring(0, 12)}...
                          </td>
                          <td className="small text-white">{job.model_used}</td>
                          <td className="small">{parseFloat(job.duration_seconds || 0).toFixed(2)}s</td>
                          <td>
                            <Badge bg={job.status === 'completed' ? 'success' : 'danger'}>
                              {job.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default ModelTesting;